import type { OptionDto, QuantityType } from "../../../../domain/types/company/company-product-system";

// ===== Label Maps =====
export const OPTION_TYPE_LABELS: Record<string, string> = {
  PAPER: "용지",
  PRINT: "인쇄",
  BINDING: "제본",
  BOOK_COVER_PAPER: "표지 용지",
  BOOK_INNER_PAPER: "내지 용지",
  BOOK_INSERT_PAPER: "간지",
  BOOK_END_PAPER: "면지",
  PAPER_ORIENTATION: "용지 방향",
};

// 추가 다이얼로그의 선택 순서이기도 하다 (대한DH 요청 순서: 무선 → 무선(날개) → 중철 → 스프링 → 떡)
export const BINDING_TYPE_LABELS: Record<string, string> = {
  BINDING_HOT_MELT: "무선제본",
  BINDING_HOT_MELT_WING: "무선제본(날개)",
  BINDING_SADDLE_STITCH: "중철제본",
  BINDING_SPRING: "스프링제본",
  BINDING_EVA: "떡제본",
  BINDING_PUR: "PUR제본",
  BINDING_CASE: "양장제본",
  BINDING_RING: "링바인더",
};

export const BINDING_DIRECTION_LABELS: Record<string, string> = {
  BINDING_DIRECTION_LEFT: "좌철",
  BINDING_DIRECTION_RIGHT: "우철",
  BINDING_DIRECTION_TOP: "상철",
};

export const PAPER_ORIENTATION_LABELS: Record<string, string> = {
  PAPER_ORIENTATION_PORTRAIT: "세로",
  PAPER_ORIENTATION_LANDSCAPE: "가로",
};

export const END_PAPER_PLACEMENT_LABELS: Record<string, string> = {
  END_PAPER_FRONT: "앞쪽 면지 1장",
  END_PAPER_BACK: "뒤쪽 면지 1장",
  END_PAPER_FRONT_AND_BACK: "앞, 뒤쪽 면지 1장씩",
  END_PAPER_FRONT_BOTH: "앞쪽 면지 2장",
  END_PAPER_BACK_BOTH: "뒤쪽 면지 2장",
  END_PAPER_FRONT_AND_BACK_BOTH: "앞, 뒤쪽 면지 2장씩",
};

export const QUANTITY_TYPE_LABELS: Record<string, string> = {
  SINGLE: "1개 고정",
  SELECTABLE: "고객이 수량 선택",
  MULTIPLIED_BY_PRODUCT_QUANTITY: "고객 선택 수량 × 주문 수량",
  SAME_AS_PRODUCT_QUANTITY: "주문 수량과 동일",
};

/**
 * 새로 만드는 옵션의 수량 방식 기본값.
 *
 * 코팅·접지·오시 같은 후가공은 장마다 비용이 들기 때문에 "1개 고정"으로 두면
 * 주문 수량과 상관없이 한 번만 청구된다. 그래서 새 옵션은 주문 수량을 따르게 한다.
 * (이미 만든 옵션은 바꾸지 않는다)
 */
export const DEFAULT_NEW_OPTION_QUANTITY_TYPE: QuantityType = "SAME_AS_PRODUCT_QUANTITY";

export const PREVIEW_TYPE_LABELS: Record<string, string> = {
  SELECT_BOX: "선택 박스",
  CHECK_BOX: "체크 박스",
  DROP_DOWN: "드롭다운",
  IMAGE_SELECT_BOX: "이미지 선택",
  INPUT_NUMBER: "숫자 입력",
  INPUT_STRING: "텍스트 입력",
  INPUT_DATE_TIME: "날짜/시간 입력",
};

export const SYSTEM_ATTRIBUTE_TYPE_LABELS: Record<string, string> = {
  BINDING_TYPE: "제본 방식",
  BINDING_DIRECTION: "제본 방향 (좌철/우철/상철)",
  PAPER_TYPE: "용지 종류",
  GRAMMAGE: "용지 두께 (평량)",
  PRINT_COLOR_MODE: "인쇄 색상 (4도/1도 등)",
  END_PAPER_PLACEMENT: "면지 위치",
  INSERT_PAGE_NUMBER: "간지 삽입 위치",
  PAPER_ORIENTATION: "용지 방향 (세로/가로)",
};

// ===== Predefined Option Templates =====
export interface PredefinedOptionTemplate {
  key: string;
  name: string;
  optionType: string | null;
  isSpecialEndpoint: boolean;
  filterByOptionType: string | null;
  bookOnly: boolean;
}

export const PREDEFINED_OPTION_TEMPLATES: PredefinedOptionTemplate[] = [
  {
    key: "insert_paper",
    name: "간지 추가",
    optionType: "BOOK_INSERT_PAPER",
    isSpecialEndpoint: true,
    filterByOptionType: "BOOK_INSERT_PAPER",
    bookOnly: true,
  },
  // 면지 옵션(BOOK_END_PAPER)은 더 이상 만들지 않는다. 양장 상품 상세의 "면지 용지"(END_PAPER)로 옮겨졌다.
  {
    key: "paper_orientation",
    name: "용지 방향",
    optionType: "PAPER_ORIENTATION",
    isSpecialEndpoint: true,
    filterByOptionType: "PAPER_ORIENTATION",
    bookOnly: false,
  },
  {
    key: "finishing",
    name: "후가공",
    optionType: null,
    isSpecialEndpoint: false,
    filterByOptionType: null,
    bookOnly: false,
  },
];

// ===== Helper: get value display label by type =====
export function getValueTypeLabel(type: string | null | undefined): string {
  if (!type) return "";
  return (
    BINDING_TYPE_LABELS[type] ??
    BINDING_DIRECTION_LABELS[type] ??
    PAPER_ORIENTATION_LABELS[type] ??
    END_PAPER_PLACEMENT_LABELS[type] ??
    type
  );
}

// ===== Helper: filter available predefined templates =====
export function getAvailableTemplates(
  productType: string,
  existingOptions: OptionDto[],
  isBook: boolean,
): PredefinedOptionTemplate[] {
  return PREDEFINED_OPTION_TEMPLATES.filter((tmpl) => {
    if (tmpl.bookOnly && !isBook) return false;
    if (tmpl.filterByOptionType) {
      const alreadyExists = existingOptions.some(
        (o) => !o.isDeleted && o.optionType === tmpl.filterByOptionType,
      );
      if (alreadyExists) return false;
    }
    return true;
  });
}

// ===== Predefined System Attributes by Option Type =====
export interface PredefinedAttributeTemplate {
  key: string;
  name: string;
  systemAttributeType: string;
}

// 옵션 타입별 추가 가능한 시스템 속성 목록
export const OPTION_TYPE_ATTRIBUTES: Record<string, PredefinedAttributeTemplate[]> = {
  BINDING: [
    { key: "binding_type", name: "제본 방식", systemAttributeType: "BINDING_TYPE" },
    { key: "binding_direction", name: "제본 방향", systemAttributeType: "BINDING_DIRECTION" },
  ],
  BOOK_COVER_PAPER: [
    { key: "paper_type", name: "용지 종류", systemAttributeType: "PAPER_TYPE" },
    { key: "grammage", name: "두께", systemAttributeType: "GRAMMAGE" },
    { key: "print_color_mode", name: "인쇄 방식", systemAttributeType: "PRINT_COLOR_MODE" },
  ],
  BOOK_INNER_PAPER: [
    { key: "paper_type", name: "용지 종류", systemAttributeType: "PAPER_TYPE" },
    { key: "grammage", name: "두께", systemAttributeType: "GRAMMAGE" },
  ],
  BOOK_INSERT_PAPER: [
    { key: "paper_type", name: "용지 종류", systemAttributeType: "PAPER_TYPE" },
    { key: "grammage", name: "두께", systemAttributeType: "GRAMMAGE" },
    { key: "insert_page_number", name: "페이지 번호", systemAttributeType: "INSERT_PAGE_NUMBER" },
  ],
  BOOK_END_PAPER: [
    { key: "paper_type", name: "용지 종류", systemAttributeType: "PAPER_TYPE" },
    { key: "grammage", name: "두께", systemAttributeType: "GRAMMAGE" },
    { key: "end_paper_placement", name: "면지 구성", systemAttributeType: "END_PAPER_PLACEMENT" },
  ],
};

// 이미 추가된 속성을 제외한 추가 가능한 속성 목록 반환
export function getAvailableAttributesForOption(
  optionType: string | null | undefined,
  existingAttributes: { systemAttributeType?: string | null; isDeleted?: boolean }[],
): PredefinedAttributeTemplate[] {
  if (!optionType) return [];

  const templates = OPTION_TYPE_ATTRIBUTES[optionType];
  if (!templates) return [];

  const existingTypes = new Set(
    existingAttributes
      .filter((a) => !a.isDeleted && a.systemAttributeType)
      .map((a) => a.systemAttributeType),
  );

  return templates.filter((t) => !existingTypes.has(t.systemAttributeType));
}
